import type { ReactNode } from 'react';
import { cardImage } from '../responsiveImage';
import { richTextPlain } from '../richText';
import { communityListingsHref, type CommunityProfile } from '../communityProfiles';

/**
 * The /communities page's live list: one card per featured community, with its active-listing
 * count and up to three of its listings.
 *
 * One component for BOTH hosts. The tenant route renders it at the page's `automatic_content`
 * block (or under its built-in header), streaming each card's count and listings in behind their
 * own Suspense boundaries through `renderCount` / `renderListings`; the Studio renders the same
 * component in the same spot with the listings it fetched, so the editor shows the cards visitors
 * see. Before this the markup lived only in the tenant route and the Studio drew a placeholder
 * ("Your featured communities appear here automatically.", Homes in Paradise SWFL, 2026-10-20).
 *
 * Framework-agnostic (plain <a>) so it transpiles in both Next.js and Vite. The `community-*`
 * class hooks take the site's Communities look (`data-tmpl-community` + cardStyles.css).
 */

/** One listing in a card's strip. */
export interface CommunityStripListing {
  id: string;
  price: number | string | null;
  city: string;
  neighborhood: string | null;
  photo: string | null;
}

/** A card: the community and the listings its strip shows when the host draws them inline. */
export type CommunityCardData = CommunityProfile & { listings?: CommunityStripListing[] };

export function CommunityCountBadge({ count, color }: { count: number; color: string }) {
  return (
    <span className="inline-flex rounded-full px-3 py-1 text-xs font-semibold text-white" style={{ backgroundColor: color }}>
      {count} active listings
    </span>
  );
}

export function CommunityListingStripSkeleton() {
  return (
    <div className="mt-4 space-y-4 animate-pulse" aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex items-center gap-4 rounded-2xl border border-gray-200 bg-white p-3">
          <div className="h-20 w-24 rounded-xl bg-gray-100" />
          <div className="flex-1 space-y-2">
            <div className="h-4 w-24 rounded bg-gray-100" />
            <div className="h-3 w-40 rounded bg-gray-100" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function CommunityListingStrip({ communityName, listings }: { communityName: string; listings: CommunityStripListing[] }) {
  if (listings.length === 0) {
    return <p className="mt-4 text-sm text-gray-500">No active listings are currently matching this community name.</p>;
  }
  return (
    <div className="mt-4 space-y-4">
      {listings.map((listing) => (
        <a
          key={listing.id}
          href={`/listings/${encodeURIComponent(listing.id)}`}
          className="flex items-center gap-4 rounded-2xl border border-gray-200 bg-white p-3 transition-shadow hover:shadow-sm"
        >
          <div className="h-20 w-24 overflow-hidden rounded-xl bg-gray-100">
            {listing.photo ? (
              // 96px wide on screen: the 640 copy, lazily. These used to be the feed's
              // full-size originals (~400 KB each, 12 MB a page).
              <img src={cardImage(listing.photo)} alt={communityName} loading="lazy" decoding="async" className="h-full w-full object-cover" />
            ) : null}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900">
              {listing.price != null && listing.price !== '' ? `$${Number(listing.price).toLocaleString()}` : 'Price on request'}
            </p>
            <p className="mt-1 text-sm text-gray-600">{[listing.neighborhood, listing.city].filter(Boolean).join(' · ')}</p>
          </div>
        </a>
      ))}
    </div>
  );
}

export interface CommunityCardListProps {
  communities: CommunityCardData[];
  primaryColor: string;
  /** The card's count badge; default: the stored count. The tenant streams the live count here. */
  renderCount?: (community: CommunityCardData) => ReactNode;
  /** The card's listing strip; default: `community.listings`, or the skeleton while they are unknown. */
  renderListings?: (community: CommunityCardData) => ReactNode;
  /** Beside the card's buttons (the live site's Compare tick box). */
  renderCompare?: (community: CommunityCardData) => ReactNode;
  /** After the cards (the live site's compare bar). */
  after?: ReactNode;
}

export function CommunityCardList({ communities, primaryColor, renderCount, renderListings, renderCompare, after }: CommunityCardListProps) {
  return (
    <section className="auto-list-band py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {communities.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-gray-500 text-lg">No featured communities yet. Check back soon.</p>
            <a
              href="/listings"
              className="mt-6 inline-block px-6 py-3 rounded-lg font-semibold text-sm text-white transition-opacity hover:opacity-90"
              style={{ backgroundColor: primaryColor }}
            >
              Browse All Listings
            </a>
          </div>
        ) : (
          <>
            <p className="text-center text-gray-500 mb-10">
              {communities.length} featured {communities.length === 1 ? 'community' : 'communities'}
            </p>
            <div className="community-grid space-y-8">
              {communities.map((community) => (
                <article
                  key={community.id}
                  className="community-card auto-list-card blk-card overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm"
                >
                  <div className="community-card-inner grid gap-0 lg:grid-cols-[1.15fr_0.85fr]">
                    <div className="community-card-main p-8 lg:p-10">
                      <div className="flex flex-wrap items-center gap-3">
                        <h2 className="text-3xl font-heading font-bold text-gray-900">{community.name}</h2>
                        {renderCount
                          ? renderCount(community)
                          : <CommunityCountBadge count={community.listing_count_active ?? 0} color={primaryColor} />}
                      </div>
                      <p className="mt-3 text-sm uppercase tracking-wide text-gray-400">
                        {[community.city, community.county, community.state].filter(Boolean).join(', ')}
                      </p>
                      <p className="community-card-desc mt-5 text-base leading-7 text-gray-600">
                        {richTextPlain(community.local_intro) || richTextPlain(community.description) || `Explore active listings in ${community.name} and browse homes tied to this community.`}
                      </p>

                      {community.highlights?.length ? (
                        <div className="community-card-highlights mt-5 flex flex-wrap gap-2">
                          {community.highlights.slice(0, 5).map((highlight) => (
                            <span
                              key={highlight}
                              className="inline-flex rounded-full border px-3 py-1 text-xs font-medium"
                              style={{ borderColor: `${primaryColor}40`, color: primaryColor }}
                            >
                              {highlight}
                            </span>
                          ))}
                        </div>
                      ) : null}

                      <div className="mt-6 flex flex-wrap gap-3">
                        <a
                          href={`/communities/${community.slug}`}
                          className="inline-flex items-center rounded-xl px-4 py-2 text-sm font-semibold text-white"
                          style={{ backgroundColor: primaryColor }}
                        >
                          View Community
                        </a>
                        <a
                          href={communityListingsHref(community)}
                          className="inline-flex items-center rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-white"
                        >
                          View Listings
                        </a>
                        {renderCompare ? renderCompare(community) : null}
                      </div>
                    </div>

                    <div className="community-card-listings border-t bg-gray-50 p-8 lg:border-l lg:border-t-0 lg:p-10">
                      <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Associated Listings</h3>
                      {renderListings
                        ? renderListings(community)
                        : community.listings
                          ? <CommunityListingStrip communityName={community.name} listings={community.listings} />
                          : <CommunityListingStripSkeleton />}
                    </div>
                  </div>
                </article>
              ))}
            </div>
            {after}
          </>
        )}
      </div>
    </section>
  );
}
